import React, { useEffect, useRef, useState } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { enqueueSnackbar } from 'notistack';

import {
  deleteGeneratedDownload,
  GeneratedDownloadClipComposer,
  GeneratedDownloadFile,
  GeneratedDownloadFolder,
  GeneratedDownloadType,
  getGeneratedDownloadFolders,
  getGeneratedDownloadClipComposers,
  getGeneratedDownloads,
  getGeneratedDownloadUrl,
} from '../../../api';
import { MoreIcon } from '../../../assets/icons/threedots';
import { Portal } from '../../../helpers/bodyPortal';
import { DateFormatter } from '../../../helpers/dateFormatter';
import useOnClickOutside from '../../../helpers/useOnClickOutside';
import { SortOptions, SortType } from '../../../enums/SORT';
import DownloadButton from '../../modules/DownloadButton';
import LinearProgress from '../../modules/LinearProgress';
import { PaginationFooter } from '../../modules/PaginationFooter';
import { OrderSelector } from '../../modules/Selector';
import ShareButton from '../../modules/ShareButton';
import TrashButton from '../../modules/TrashButton';
import styles from './GeneratedDownloads.module.scss';

const folderDefinitions: Array<{ type: GeneratedDownloadType; title: string }> = [
  { type: 'standaloneMovies', title: 'Standalone Movies' },
  { type: 'standaloneMoviesWithPadding', title: 'Standalone Movies with Padding' },
  { type: 'clips', title: 'Individual Clips' },
  { type: 'mergedTimelineVideos', title: 'Merged Timeline Videos' },
];

const sortOptions = [
  { label: 'A-Z ↓', value: SortOptions.NAME, order: SortType.ASC },
  { label: 'Z-A ↑', value: SortOptions.NAME, order: SortType.DESC },
  { label: 'Size ↓', value: SortOptions.SIZE, order: SortType.DESC },
  { label: 'Size ↑', value: SortOptions.SIZE, order: SortType.ASC },
  { label: 'Generated ↓', value: SortOptions.UPDATED_AT, order: SortType.DESC },
  { label: 'Generated ↑', value: SortOptions.UPDATED_AT, order: SortType.ASC },
];

const formatBytes = (bytes: number) => {
  if (!bytes) return '0 MB';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / 1024 ** index).toFixed(index > 1 ? 1 : 0)} ${units[index]}`;
};

const copyText = async (value: string) => {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();

    let copied = false;
    try {
      copied = document.execCommand('copy');
    } catch {
      copied = false;
    }
    document.body.removeChild(textarea);
    return copied;
  }
};

const GeneratedDownloads = () => {
  const [folders, setFolders] = useState<GeneratedDownloadFolder[]>([]);
  const [selectedType, setSelectedType] = useState<GeneratedDownloadType | null>(null);
  const [clipComposers, setClipComposers] = useState<GeneratedDownloadClipComposer[]>([]);
  const [selectedClipComposer, setSelectedClipComposer] = useState<GeneratedDownloadClipComposer | null>(null);
  const [files, setFiles] = useState<GeneratedDownloadFile[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [loading, setLoading] = useState(true);
  const [actionFile, setActionFile] = useState<GeneratedDownloadFile | null>(null);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [sortOption, setSortOption] = useState(SortOptions.UPDATED_AT);
  const [sortOrder, setSortOrder] = useState(SortType.DESC);
  const actionMenuRef = useRef<HTMLDivElement | null>(null);

  const fetchFolders = async () => {
    try {
      setFolders(await getGeneratedDownloadFolders());
    } catch {
      enqueueSnackbar('Failed to load generated download folders', { variant: 'error' });
    }
  };

  const fetchFiles = async () => {
    if (!selectedType || (selectedType === 'clips' && !selectedClipComposer)) return;
    setLoading(true);
    try {
      const response = await getGeneratedDownloads({
        type: selectedType,
        page,
        limit,
        sort: sortOption,
        order: sortOrder,
        ...(selectedClipComposer ? { composerId: selectedClipComposer.id } : {}),
      });
      setFiles(response.documents);
      setTotal(response.total);
    } catch {
      enqueueSnackbar('Failed to load generated downloads', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFolders().finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchFiles();
  }, [selectedType, selectedClipComposer, page, limit, sortOption, sortOrder]);

  useEffect(() => {
    if (selectedType !== 'clips' || selectedClipComposer) return;
    setLoading(true);
    getGeneratedDownloadClipComposers()
      .then(setClipComposers)
      .catch(() => enqueueSnackbar('Failed to load composer folders', { variant: 'error' }))
      .finally(() => setLoading(false));
  }, [selectedType, selectedClipComposer]);

  const closeMenu = () => {
    setActionFile(null);
  };

  const openMenu = (event: React.MouseEvent<HTMLElement>, file: GeneratedDownloadFile) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const menuWidth = 248;
    const menuHeight = 150;
    const padding = 12;
    const x = Math.min(
      Math.max(rect.right - menuWidth, padding),
      window.innerWidth - menuWidth - padding,
    );
    const y =
      window.innerHeight - rect.bottom >= menuHeight + padding
        ? rect.bottom + 8
        : Math.max(padding, rect.top - menuHeight - 8);
    setMenuPosition({ x, y });
    setActionFile(file);
  };

  useOnClickOutside(actionMenuRef, closeMenu);

  const openFolder = (type: GeneratedDownloadType) => {
    setLoading(true);
    setPage(0);
    setSelectedType(type);
  };

  const closeFolder = () => {
    if (selectedClipComposer) {
      setSelectedClipComposer(null);
      setFiles([]);
      setTotal(0);
      setPage(0);
      return;
    }
    setSelectedType(null);
    setClipComposers([]);
    setFiles([]);
    setTotal(0);
  };

  const sortHandler = (
    field: SortOptions,
    option?: { label: string; value: string; order: SortType },
  ) => {
    const nextField = option ? (option.value as SortOptions) : field;
    const nextOrder = option
      ? option.order
      : sortOption === field
        ? sortOrder === SortType.ASC
          ? SortType.DESC
          : SortType.ASC
        : SortType.ASC;
    setPage(0);
    setSortOption(nextField);
    setSortOrder(nextOrder);
  };

  const renderSortArrow = (field: SortOptions) => {
    if (sortOption !== field) return null;
    return sortOrder === SortType.DESC ? (
      <KeyboardArrowDownIcon className={styles.sortArrow} />
    ) : (
      <KeyboardArrowUpIcon className={styles.sortArrow} />
    );
  };

  const downloadFile = async () => {
    if (!actionFile) return;
    try {
      const { signedUrl } = await getGeneratedDownloadUrl(actionFile.key, actionFile.name);
      window.location.assign(signedUrl);
    } catch {
      enqueueSnackbar('Failed to download file', { variant: 'error' });
    } finally {
      closeMenu();
    }
  };

  const shareFile = async () => {
    if (!actionFile) return;
    try {
      const { signedUrl } = await getGeneratedDownloadUrl(actionFile.key, actionFile.name);
      const copied = await copyText(signedUrl);
      if (copied) {
        enqueueSnackbar('Download URL copied. It is valid for 24 hours.', { variant: 'success' });
      } else {
        window.prompt('Copy download URL:', signedUrl);
        enqueueSnackbar('Clipboard blocked. Copy the URL from the prompt.', { variant: 'warning' });
      }
    } catch {
      enqueueSnackbar('Failed to create download URL', { variant: 'error' });
    } finally {
      closeMenu();
    }
  };

  const deleteFile = async () => {
    if (!actionFile) return;
    const file = actionFile;
    closeMenu();
    if (!window.confirm(`Delete ${file.name}?`)) return;

    try {
      await deleteGeneratedDownload(file.key);
      const previousPage = page > 0 && files.length === 1 ? page - 1 : page;
      if (previousPage === page) {
        await fetchFiles();
      } else {
        setPage(previousPage);
      }
      await fetchFolders();
      enqueueSnackbar('Generated download deleted', { variant: 'success' });
    } catch {
      enqueueSnackbar('Failed to delete generated download', { variant: 'error' });
    }
  };

  const selectedFolder = folderDefinitions.find((folder) => folder.type === selectedType);
  const selectedTitle = selectedClipComposer?.title || selectedFolder?.title;

  return (
    <div className={`${styles.downloads} CustomScrollbar`}>
      <div className={styles.downloadsHeader}>
        <div className={styles.titleBlock}>
          {selectedFolder && (
            <button type="button" className={styles.backButton} onClick={closeFolder}>
              <ArrowBackIcon /> Back
            </button>
          )}
          <h2>{selectedTitle || 'Generated Downloads'}</h2>
          <p>
            {selectedType === 'mergedTimelineVideos'
              ? 'View and manage generated merged MP4s.'
              : selectedTitle
              ? `View and manage generated ${selectedTitle.toLowerCase()}.`
              : "View and manage all downloadable content files generated and stored in your organisation's S3."}
          </p>
        </div>
        {selectedFolder && (selectedType !== 'clips' || selectedClipComposer) && (
          <div className={styles.sortToolbar}>
            <OrderSelector
              title="Sorting by"
              selectedValue={sortOption}
              selectedOrder={sortOrder}
              options={sortOptions}
              onSelect={sortHandler}
            />
          </div>
        )}
      </div>

      {loading && <LinearProgress />}
      {!loading && !selectedFolder && (
        <div className={styles.folderList}>
          {folderDefinitions.map((folder) => (
            <button type="button" className={styles.folderRow} key={folder.type} onClick={() => openFolder(folder.type)}>
              <FolderOpenIcon />
              <span className={styles.folderName}>{folder.title}</span>
              <span className={styles.folderCount}>{folders.find((item) => item.type === folder.type)?.count || 0}</span>
              <ArrowForwardIosIcon className={styles.folderArrow} />
            </button>
          ))}
        </div>
      )}

      {!loading && selectedType === 'clips' && !selectedClipComposer && (
        <div className={styles.folderList}>
          {clipComposers.map((composer) => (
            <button
              type="button"
              className={styles.folderRow}
              key={composer.id}
              onClick={() => {
                setLoading(true);
                setPage(0);
                setSelectedClipComposer(composer);
              }}
            >
              <FolderOpenIcon />
              <span className={styles.folderName}>{composer.title}</span>
              <span className={styles.folderCount}>{composer.count}</span>
              <ArrowForwardIosIcon className={styles.folderArrow} />
            </button>
          ))}
          {clipComposers.length === 0 && <div className={styles.empty}>No generated clips</div>}
        </div>
      )}

      {!loading && selectedFolder && (selectedType !== 'clips' || selectedClipComposer) && (
        <>
          <div className={styles.table}>
            <div className={styles.tableHeader}>
              <button type="button" onClick={() => sortHandler(SortOptions.NAME)}>
                Name {renderSortArrow(SortOptions.NAME)}
              </button>
              <button type="button" onClick={() => sortHandler(SortOptions.SIZE)}>
                File Size {renderSortArrow(SortOptions.SIZE)}
              </button>
              <span>Generated By</span>
              <button type="button" onClick={() => sortHandler(SortOptions.UPDATED_AT)}>
                Generated {renderSortArrow(SortOptions.UPDATED_AT)}
              </button>
              <span>Actions</span>
            </div>
            {files.map((file) => (
              <div className={styles.row} key={file.key} title={file.key}>
                <span className={styles.name}>{file.name}</span>
                <span>{formatBytes(file.size)}</span>
                <span>{file.generatedBy || 'Unknown'}</span>
                <span>{file.lastModified ? DateFormatter(file.lastModified, true) : 'None'}</span>
                <span className={styles.actions}>
                  <button type="button" onClick={(event) => openMenu(event, file)} aria-label={`Actions for ${file.name}`}>
                    <MoreIcon />
                  </button>
                </span>
              </div>
            ))}
            {files.length === 0 && <div className={styles.empty}>No generated files</div>}
          </div>
          <PaginationFooter
            hideVersion
            page={page}
            total={total}
            rowsPerPage={limit}
            onPageChange={(_, nextPage) => setPage(nextPage)}
            onRowsPerPageChange={(event) => {
              setPage(0);
              setLimit(Number(event.target.value));
            }}
          />
        </>
      )}

      {actionFile && (
        <Portal>
          <div
            ref={actionMenuRef}
            className={styles.actionMenu}
            onClick={(event) => event.stopPropagation()}
            style={{ top: `${menuPosition.y}px`, left: `${menuPosition.x}px` }}
          >
            <div className={styles.menuOption} onClick={downloadFile}>
              <DownloadButton /> Download
            </div>
            <div className={styles.menuOption} onClick={deleteFile}>
              <TrashButton /> Delete
            </div>
            <div className={styles.menuOption} onClick={shareFile}>
              <ShareButton /> Share via URL
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
};

export default GeneratedDownloads;
